import { useRef } from "react";
import { useQueries } from "@tanstack/react-query";

import { getCourse } from "@/features/courses/api";
import { useGetAllCourses } from "@/features/courses/hooks/useGetAllCourses";

// How many lessons the hero plays before it stops and settles on a thumbnail.
export const HERO_VIDEO_COUNT = 3;

const STALE_TIME_MS = 5 * 60 * 1000;
const GC_TIME_MS = 10 * 60 * 1000;

/**
 * Draws without replacement, so the hero never plays the same course twice in
 * one run. Copies the pool rather than shuffling it: `courses` belongs to
 * react-query's cache and every other rail on the page reads the same array.
 */
function sample(items, count) {
	const pool = [...items];
	const picked = [];

	while (picked.length < count && pool.length > 0) {
		const index = Math.floor(Math.random() * pool.length);
		picked.push(pool.splice(index, 1)[0]);
	}

	return picked;
}

/**
 * Picks HERO_VIDEO_COUNT courses at random and returns the Bunny ids of each
 * one's first unit, so the landing hero plays a short run of real lessons
 * rather than one canned promo clip on an endless loop.
 *
 * Audio courses are excluded — they are the majority of the catalogue (66 of 95
 * at time of writing) and have nothing worth showing in a video panel.
 *
 * Every other course is fair game: their first unit carries is_preview, so the
 * product already treats it as publicly viewable. Nothing behind the paywall is
 * exposed by this.
 *
 * The picks are locked on the first render that has a catalogue and never
 * re-rolled — otherwise an unrelated re-render would swap a video out mid-play.
 *
 * NOTE ON IDENTITY: the returned array is rebuilt every render. Consumers must
 * key their effects on primitives out of it (a video id, a course name), never
 * on the array or on an entry — an object dependency changes identity on every
 * render, which is what previously restarted the hero's overlay timers and made
 * the lecturer card cycle instead of showing once.
 */
export function useHeroVideos() {
	const { courses = [] } = useGetAllCourses();
	const pickedSlugs = useRef(null);

	if (!pickedSlugs.current && courses.length > 0) {
		const videoCourses = courses.filter((course) => !course.is_audio);

		pickedSlugs.current = sample(videoCourses, HERO_VIDEO_COUNT).map(
			(course) => course.slug
		);
	}

	const slugs = pickedSlugs.current ?? [];

	// Deliberately not useApiGet: that hook wraps a single query, and its
	// error path — the Bosnian toast and the redirect — is exactly what the
	// old call site opted out of with `suppressErrorRedirect`. A hero that
	// cannot load is a hero that keeps its placeholder panel, silently.
	const results = useQueries({
		queries: Array.from({ length: HERO_VIDEO_COUNT }, (unused, position) => {
			const slug = slugs[position] ?? null;

			return {
				queryKey: ["hero-video", slug],
				queryFn: () => getCourse({ courseSlug: slug }),
				enabled: Boolean(slug),
				retry: false,
				refetchOnMount: false,
				staleTime: STALE_TIME_MS,
				gcTime: GC_TIME_MS,
			};
		}),
	});

	// Nothing comes out until every pick has settled. The alternative — handing
	// back entries as they arrive — lets a slot that resolves second land in
	// first place, which would swap the opening video after it had already
	// started. The three requests are parallel, so the wait is the slowest of
	// them rather than their sum, and the panel shows its placeholder for it.
	const settled = results.every(
		(result, position) => !slugs[position] || !result.isPending
	);

	if (slugs.length === 0 || !settled) return [];

	return slugs
		.map((slug, position) => {
			const data = results[position]?.data;
			const video = data?.video;

			// A course whose entry unit is a quiz, or one whose detail call
			// failed, drops out of the run: the hero plays the rest.
			if (!video?.bunny_library_id || !video?.bunny_video_id) return null;

			const lecturer = data?.course?.lecturers?.[0] ?? null;
			const entry = courses.find((course) => course.slug === slug) ?? null;

			return {
				// The panel links through to this course, so the slug the hook
				// already picked has to come back out with the rest.
				courseSlug: slug,
				libraryId: video.bunny_library_id,
				videoId: video.bunny_video_id,
				unitName: video.name ?? "",
				english_unitName: video.english_name ?? null,
				courseName: video.module?.course?.name ?? "",
				english_courseName: video.module?.course?.english_name ?? null,
				// Shown as a poster until the player actually starts — and
				// again, for the last of the run, once the hero has played
				// itself out. Taken from the catalogue entry we already hold,
				// so it costs no extra request.
				thumbnail:
					entry?.thumbnail ??
					video.module?.course?.thumbnail ??
					null,
				// Credited in a lower third once the video is under way. Null
				// when the course has no lecturer on record, so no card shows.
				lecturer: lecturer?.name
					? {
							name: lecturer.name,
							jobTitle: lecturer.job_title ?? "",
							english_jobTitle: lecturer.english_job_title ?? null,
							pictureUrl: lecturer.picture_url ?? null,
						}
					: null,
			};
		})
		.filter(Boolean);
}
